<template>
  <div class="company-parking-spaces">
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
          <h1 class="display-1 font-weight-bold mb-2">Cajones de Estacionamiento</h1>
          <p class="subtitle grey--text">Asigna los cajones de tu empresa a tus empleados. Cada empleado puede tener un solo cajón.</p>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="generalError" class="mb-3">
        <v-flex xs12>
          <v-alert type="error" dismissible :value="true" @input="generalError = ''">
            {{ generalError }}
          </v-alert>
        </v-flex>
      </v-layout>

      <v-layout row wrap class="mb-4">
        <v-flex xs12 sm6>
          <v-text-field
            v-model="search"
            prepend-icon="search"
            label="Buscar cajón, tarjeta o empleado..."
            clearable
            dense
          ></v-text-field>
        </v-flex>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs12>
          <v-card class="elevation-2">
            <v-data-table
              :headers="headers"
              :items="filteredSpaces"
              :loading="loading"
              :rows-per-page-items="[10, 20, 50]"
              :rows-per-page="20"
              no-data-text="Tu empresa no tiene cajones asignados"
              class="elevation-1"
            >
              <template v-slot:items="props">
                <td class="text-xs-center">{{ props.item.code }}</td>
                <td class="text-xs-center">{{ props.item.access_card_number || 'Sin tarjeta' }}</td>
                <td class="text-xs-center">
                  <v-chip :color="getStatusColor(props.item.status)" text-color="white" small>
                    {{ props.item.status }}
                  </v-chip>
                </td>
                <td class="text-xs-center">{{ props.item.employee_name || 'Sin asignar' }}</td>
                <td class="text-xs-center">
                  <v-btn
                    small
                    color="primary"
                    :disabled="isLocked(props.item)"
                    @click="openAssign(props.item)"
                  >
                    {{ props.item.employee ? 'Cambiar' : 'Asignar' }}
                  </v-btn>
                  <v-btn
                    v-if="props.item.employee"
                    small
                    color="error"
                    :loading="savingId === props.item.id"
                    @click="release(props.item)"
                  >
                    Liberar
                  </v-btn>
                </td>
              </template>
              <template v-slot:no-results>
                <v-alert type="info" :value="true">No se encontraron cajones</v-alert>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog v-model="dialog" max-width="480px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">Asignar cajón {{ selectedSpace ? selectedSpace.code : '' }}</span>
        </v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="selectedEmployee"
            :items="employeeItems"
            item-text="label"
            item-value="id"
            item-disabled="disabled"
            label="Empleado"
            prepend-icon="person"
            :error-messages="dialogError"
            no-data-text="No hay empleados"
            clearable
          ></v-autocomplete>
          <p class="caption grey--text">Los empleados que ya tienen otro cajón aparecen deshabilitados.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" flat @click="closeDialog">Cancelar</v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedEmployee"
            :loading="savingId !== null"
            @click="assign"
          >
            Asignar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import '@/plugins/axios'
import axios from 'axios'
import store from '@/store'

const API_BASE_URL = 'https://empresa-service.intrare.app'

export default {
  name: 'CompanyParkingSpaces',
  data() {
    return {
      spaces: [],
      employees: [],
      loading: false,
      savingId: null,
      search: '',
      dialog: false,
      selectedSpace: null,
      selectedEmployee: null,
      dialogError: '',
      generalError: '',
      headers: [
        { text: 'Cajón', value: 'code', align: 'center' },
        { text: 'Tarjeta', value: 'access_card_number', align: 'center' },
        { text: 'Estado', value: 'status', align: 'center' },
        { text: 'Empleado', value: 'employee_name', align: 'center' },
        { text: 'Acciones', value: 'actions', align: 'center', sortable: false }
      ]
    }
  },
  computed: {
    filteredSpaces() {
      const search = (this.search || '').toLowerCase().trim()
      if (!search) return this.spaces
      return this.spaces.filter(space =>
        [space.code, space.access_card_number, space.employee_name]
          .some(value => value && String(value).toLowerCase().includes(search))
      )
    },
    employeeItems() {
      const currentSpaceId = this.selectedSpace ? this.selectedSpace.id : null
      return this.employees.map(employee => ({
        id: employee.id,
        label: employee.parking_space_code && employee.parking_space !== currentSpaceId
          ? `${employee.name} — cajón ${employee.parking_space_code}`
          : employee.name,
        disabled: !!employee.parking_space && employee.parking_space !== currentSpaceId
      }))
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    authHeaders() {
      return {
        'Content-Type': 'application/json',
        'authorization': 'Token ' + store.getters.getToken
      }
    },
    fetchData() {
      this.loading = true
      this.generalError = ''
      return Promise.all([
        axios.get(`${API_BASE_URL}/Parking/company/spaces/`, { headers: this.authHeaders() }),
        axios.get(`${API_BASE_URL}/Parking/company/employees/`, { headers: this.authHeaders() })
      ])
        .then(([spacesResponse, employeesResponse]) => {
          this.spaces = spacesResponse.data
          this.employees = employeesResponse.data
        })
        .catch(error => {
          this.generalError = this.errorMessage(error, 'Error al cargar los cajones')
        })
        .finally(() => {
          this.loading = false
        })
    },
    isLocked(space) {
      return space.status === 'bloqueado' || space.status === 'mantenimiento'
    },
    openAssign(space) {
      this.selectedSpace = space
      this.selectedEmployee = space.employee || null
      this.dialogError = ''
      this.dialog = true
    },
    closeDialog() {
      this.dialog = false
      this.selectedSpace = null
      this.selectedEmployee = null
      this.dialogError = ''
    },
    assign() {
      this.patchEmployee(this.selectedSpace, this.selectedEmployee)
    },
    release(space) {
      this.patchEmployee(space, null)
    },
    patchEmployee(space, employeeId) {
      this.savingId = space.id
      this.dialogError = ''
      axios({
        method: 'patch',
        url: `${API_BASE_URL}/Parking/company/spaces/${space.id}/employee/`,
        headers: this.authHeaders(),
        data: { employee: employeeId }
      })
        .then(() => {
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit(
            'snackbar_change_message',
            employeeId ? `Cajón ${space.code} asignado` : `Cajón ${space.code} liberado`
          )
          this.$store.commit('snackbar_activate_true')
          this.closeDialog()
          return this.fetchData()
        })
        .catch(error => {
          const data = error.response && error.response.data
          const message = this.errorMessage(error, 'Error al actualizar la asignación')
          if (this.dialog) {
            this.dialogError = data && data.employee ? [].concat(data.employee) : [message]
          } else {
            this.generalError = message
          }
        })
        .finally(() => {
          this.savingId = null
        })
    },
    errorMessage(error, fallback) {
      const data = error.response && error.response.data
      if (!data) return fallback
      if (data.employee) return [].concat(data.employee)[0]
      return data.error || data.detail || fallback
    },
    getStatusColor(status) {
      const colors = {
        disponible: 'success',
        ocupado: 'warning',
        bloqueado: 'error',
        mantenimiento: 'info'
      }
      return colors[status] || 'grey'
    }
  }
}
</script>

<style scoped>
.company-parking-spaces {
  padding: 20px;
}
</style>
